import { useCallback, useMemo } from 'react';

import { useApolloClient } from '@apollo/client/react';
import { useAuthContext } from 'platform/components/AuthContext';
import { useParams } from 'react-router';

import { SellerTransactionProductsDocument } from './__generated__/SellerTransactionProducts';
import { useSellerLead } from './SellerLeadContext';

/**
 * Returns the `refetchQueries` value for mutations that change seller-product
 * state. Refetches `sellerTransaction.acquisitionProducts` /
 * `selectedAcquisitionProduct` so post-mutation reads see fresh product data
 * in a single Apollo cache broadcast.
 *
 * Note: legacy `productsExperiences.previouslyEligibleProducts` is *not*
 * refetched here — that field only changes when a customer "walks" off a
 * product, which the cake-side mutations never trigger.
 */
export const useSellerTransactionProductsRefetchQueries = () => {
  const { addressUUID } = useParams();
  const { customerUUID } = useAuthContext();
  const { lead } = useSellerLead();
  const sellerLeadUuid = lead?.id;

  return useMemo(() => {
    if (!addressUUID || !sellerLeadUuid) {
      return [];
    }
    return [
      {
        query: SellerTransactionProductsDocument,
        variables: {
          addressUUID,
          ...(customerUUID !== undefined ? { customerUUID } : {}),
        },
      },
    ];
  }, [addressUUID, customerUUID, sellerLeadUuid]);
};

/**
 * Imperative refetch of the same query, for callers outside a mutation's
 * `refetchQueries` (e.g. the agent walkthrough's manual product refresh
 * via `useSellerProducts.refetch`).
 */
export const useRefetchSellerTransactionProducts = () => {
  const client = useApolloClient();
  const { addressUUID } = useParams();
  const { customerUUID } = useAuthContext();
  const { lead } = useSellerLead();
  const sellerLeadUuid = lead?.id;

  return useCallback(async () => {
    if (!addressUUID || !sellerLeadUuid) {
      return;
    }
    await client.query({
      query: SellerTransactionProductsDocument,
      variables: {
        addressUUID,
        ...(customerUUID !== undefined ? { customerUUID } : {}),
      },
      fetchPolicy: 'network-only',
    });
  }, [client, addressUUID, customerUUID, sellerLeadUuid]);
};
